import { EventEmitter } from 'events';
import { WhatsAppClient } from '../client/WhatsAppClient';

export interface BotCommand {
  name: string;
  description: string;
  usage?: string;
  permissions?: string[];
  handler: (context: BotContext, args: string[]) => Promise<string | void>;
}

export interface BotContext {
  message: any;
  client: WhatsAppClient;
  tel: string;
  chatId: string;
  senderId: string;
  isGroup: boolean;
  user?: any;
  db?: any;
  [key: string]: any;
}

export interface BotMiddleware {
  (context: BotContext, next: () => Promise<void>): Promise<void>;
}

export interface BotConfig {
  prefix: string;
  caseSensitive?: boolean;
  defaultResponse?: string;
  maxCommandsPerMinute?: number;
  permissionDeniedMessage?: string;
  commandNotFoundMessage?: string;
  errorMessage?: string;
}

export class BotFramework extends EventEmitter {
  private commands: Map<string, BotCommand> = new Map();
  private middleware: BotMiddleware[] = [];
  private rateLimits: Map<string, number[]> = new Map();
  private config: Required<BotConfig>;

  constructor(
    private client: WhatsAppClient,
    private tel: string,
    config: BotConfig
  ) {
    super();
    this.config = {
      caseSensitive: false,
      defaultResponse: 'Command not found. Type !help for available commands.',
      maxCommandsPerMinute: 10,
      permissionDeniedMessage: 'You do not have permission to use this command.',
      commandNotFoundMessage: 'Command not found. Type !help for available commands.',
      errorMessage: 'An error occurred while processing your command.',
      ...config
    };

    // Add default help command
    this.addCommand({
      name: 'help',
      description: 'Show available commands',
      usage: `${this.config.prefix}help [command]`,
      handler: async (context, args) => {
        if (args.length > 0) {
          return this.getCommandHelp(args[0]);
        }
        return this.getHelpText();
      }
    });
  }

  /**
   * Add a command to the bot
   */
  addCommand(command: BotCommand): void {
    const commandName = this.config.caseSensitive ? command.name : command.name.toLowerCase();
    this.commands.set(commandName, command);
    this.emit('command:added', command);
  }

  /**
   * Remove a command from the bot
   */
  removeCommand(name: string): boolean {
    const commandName = this.config.caseSensitive ? name : name.toLowerCase();
    const removed = this.commands.delete(commandName);
    if (removed) {
      this.emit('command:removed', name);
    }
    return removed;
  }

  /**
   * Add middleware to the bot
   */
  use(middleware: BotMiddleware): void {
    this.middleware.push(middleware);
  }

  /**
   * Process an incoming message
   */
  async processMessage(message: any): Promise<void> {
    try {
      // Check if message starts with prefix
      const text = message.message?.conversation || message.message?.extendedTextMessage?.text || '';
      if (!text.startsWith(this.config.prefix)) {
        return;
      }

      // Parse command and arguments
      const commandText = text.slice(this.config.prefix.length).trim();
      const [commandName, ...args] = commandText.split(/\s+/);
      
      if (!commandName) {
        return;
      }

      // Create context
      const context: BotContext = {
        message,
        client: this.client,
        tel: this.tel,
        chatId: message.key.remoteJid,
        senderId: message.key.participant || message.key.remoteJid,
        isGroup: message.key.remoteJid.endsWith('@g.us')
      };

      // Check rate limiting
      if (!this.checkRateLimit(context.senderId)) {
        await this.sendResponse(context, 'You are sending commands too quickly. Please wait before trying again.');
        return;
      }

      // Find command
      const lookupName = this.config.caseSensitive ? commandName : commandName.toLowerCase();
      const command = this.commands.get(lookupName);

      if (!command) {
        await this.sendResponse(context, this.config.commandNotFoundMessage);
        return;
      }

      // Execute middleware chain
      let middlewareIndex = 0;
      const executeMiddleware = async (): Promise<void> => {
        if (middlewareIndex >= this.middleware.length) {
          // Execute command
          await this.executeCommand(command, context, args);
          return;
        }

        const middleware = this.middleware[middlewareIndex++];
        await middleware(context, executeMiddleware);
      };

      await executeMiddleware();

    } catch (error) {
      this.emit('error', error);
      console.error('Bot error:', error);
    }
  }

  /**
   * Execute a command
   */
  private async executeCommand(command: BotCommand, context: BotContext, args: string[]): Promise<void> {
    try {
      // Check permissions
      if (command.permissions && command.permissions.length > 0) {
        if (!context.user || !this.hasPermissions(context.user, command.permissions)) {
          await this.sendResponse(context, this.config.permissionDeniedMessage);
          return;
        }
      }

      this.emit('command:start', { command: command.name, context, args });

      // Execute command handler
      const response = await command.handler(context, args);
      
      if (response) {
        await this.sendResponse(context, response);
      }

      this.emit('command:complete', { command: command.name, context, args, response });

    } catch (error) {
      this.emit('command:error', { command: command.name, context, args, error });
      await this.sendResponse(context, this.config.errorMessage);
      throw error instanceof Error ? error : new Error(String(error));
    }
  }

  /**
   * Send response to chat
   */
  private async sendResponse(context: BotContext, response: string): Promise<void> {
    try {
      await this.client.messages.sendText(context.tel, context.chatId, response);
    } catch (error) {
      this.emit('error', error);
      console.error('Failed to send bot response:', error);
    }
  }

  /**
   * Check rate limiting for user
   */
  private checkRateLimit(userId: string): boolean {
    const now = Date.now();
    const userLimits = this.rateLimits.get(userId) || [];
    
    // Remove timestamps older than 1 minute
    const recentCommands = userLimits.filter(timestamp => now - timestamp < 60000);
    
    if (recentCommands.length >= this.config.maxCommandsPerMinute) {
      return false;
    }

    // Add current timestamp
    recentCommands.push(now);
    this.rateLimits.set(userId, recentCommands);
    
    return true;
  }

  /**
   * Check if user has required permissions
   */
  private hasPermissions(user: any, requiredPermissions: string[]): boolean {
    if (!user.permissions) {
      return false;
    }

    return requiredPermissions.every(permission => 
      user.permissions.includes(permission) || user.permissions.includes('*')
    );
  }

  /**
   * Get help text for all commands
   */
  private getHelpText(): string {
    const commandList = Array.from(this.commands.values())
      .map(cmd => `${this.config.prefix}${cmd.name} - ${cmd.description}`)
      .join('\n');

    return `Available commands:\n${commandList}`;
  }

  /**
   * Get help text for specific command
   */
  private getCommandHelp(commandName: string): string {
    const lookupName = this.config.caseSensitive ? commandName : commandName.toLowerCase();
    const command = this.commands.get(lookupName);

    if (!command) {
      return `Command "${commandName}" not found.`;
    }

    let help = `*${command.name}*\n${command.description}`;
    
    if (command.usage) {
      help += `\n\nUsage: ${command.usage}`;
    }

    if (command.permissions && command.permissions.length > 0) {
      help += `\n\nRequired permissions: ${command.permissions.join(', ')}`;
    }

    return help;
  }

  /**
   * Get all registered commands
   */
  getCommands(): BotCommand[] {
    return Array.from(this.commands.values());
  }

  /**
   * Start listening for messages
   */
  start(): void {
    this.emit('bot:started', { tel: this.tel, prefix: this.config.prefix });
  }

  /**
   * Stop the bot
   */
  stop(): void {
    this.removeAllListeners();
    this.commands.clear();
    this.middleware.length = 0;
    this.rateLimits.clear();
    this.emit('bot:stopped', { tel: this.tel });
  }
}

/**
 * Built-in middleware functions
 */
export const middleware = {
  /**
   * Add user context from database
   */
  userContext: (getUserFn: (senderId: string) => Promise<any>) => {
    return async (context: BotContext, next: () => Promise<void>) => {
      try {
        context.user = await getUserFn(context.senderId);
      } catch (error) {
        console.error('Failed to load user context:', error);
      }
      await next();
    };
  },

  /**
   * Add database context
   */
  databaseContext: (db: any) => {
    return async (context: BotContext, next: () => Promise<void>) => {
      context.db = db;
      await next();
    };
  },

  /**
   * Language detection middleware
   */
  languageDetection: () => {
    return async (context: BotContext, next: () => Promise<void>) => {
      const text = context.message.message?.conversation || context.message.message?.extendedTextMessage?.text || '';
      // Simple language detection (can be enhanced with proper library)
      context.language = /[^\u0000-\u007F]/.test(text) ? 'non-english' : 'english';
      await next();
    };
  },

  /**
   * Logging middleware
   */
  logging: (logger?: (level: string, message: string, data?: any) => void) => {
    const log = logger || ((level, message, data) => console.log(`[${level}] ${message}`, data || ''));
    
    return async (context: BotContext, next: () => Promise<void>) => {
      const startTime = Date.now();
      const text = context.message.message?.conversation || context.message.message?.extendedTextMessage?.text || '';
      
      log('info', 'Bot command received', {
        senderId: context.senderId,
        chatId: context.chatId,
        text: text.substring(0, 100)
      });

      try {
        await next();
        const duration = Date.now() - startTime;
        log('info', 'Bot command completed', { duration });
      } catch (error) {
        const duration = Date.now() - startTime;
        log('error', 'Bot command failed', { duration, error: error instanceof Error ? error.message : String(error) });
        throw error;
      }
    };
  },

  /**
   * Authentication middleware
   */
  authentication: (isAuthorizedFn: (context: BotContext) => Promise<boolean>) => {
    return async (context: BotContext, next: () => Promise<void>) => {
      const isAuthorized = await isAuthorizedFn(context);
      if (!isAuthorized) {
        throw new Error('Unauthorized command access');
      }
      await next();
    };
  }
};